import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { json, redirect } from '@remix-run/node'
import { type MetaFunction, useLoaderData } from '@remix-run/react'
import { useState } from 'react'
import { serverOnly$ } from 'vite-env-only'
import AdminOffices from '#app/components/commonElements/AdminOffices.tsx'
import { PaginationBar } from '#app/components/commonElements/PaginationBar.tsx'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import PageTitle from '#app/components/pageLayout/PageTitle.tsx'
import ProcedureListElement from '#app/components/procedures/ProcedureListElement.tsx'
import { SearchBar } from '#app/components/search-bar.tsx'
import { Breadcrumb } from '#app/components/tramite/breadcrumb.tsx'
import {
	getAdministration,
	getAllAdministrations,
} from '#app/routes/_appAdmin+/administrations/_administrationQueries.server.ts'
import { getRedirection } from '#app/routes/_appAdmin+/redirections/_redirectionsQueries.server.ts'
import { invariantResponse } from '#app/utils/misc.tsx'
import { getAdministrationProcedureCount } from '../../_appAdmin+/procedures/_proceduresQueries.server'

export async function loader({ params, request }: DataFunctionArgs) {
	const url = new URL(request.url)
	const page = Number(url.searchParams.get('page')) || 1
	const pageSize = 10
	const skip = (page - 1) * pageSize
	const searchTerm = url.searchParams.get('search')
	const tabTerm = url.searchParams.get('tab')

	if (searchTerm === '') {
		return redirect(url.pathname)
	}

	const [administration, totalCount] = await Promise.all([
		getAdministration(
			String(params.country),
			String(params.administration),
			String(searchTerm ?? ''),
			pageSize,
			skip,
		),
		getAdministrationProcedureCount(
			String(params.administration),
			searchTerm ?? '',
		),
	])

	if (!administration) {
		const redirection = await getRedirection(url.pathname)
		if (redirection) {
			return redirect(redirection.newUrl)
		}
	}

	invariantResponse(administration, 'Not found', { status: 404 })
	return json({ 
		status: 'idle', 
		administration, 
		tabTerm, 
		totalCount, 
		page, 
		pageSize 
	})
}

export default function Administration() {
	const data = useLoaderData<typeof loader>()
	const { administration } = data

	const procedures = administration?.procedures.map(procedure => {
		const dateObject = new Date(procedure.updatedAt)
		const formattedDate = new Intl.DateTimeFormat('es-ES', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
		}).format(dateObject)
		return { ...procedure, formattedDate: `Actualizado el ${formattedDate}` }
	})

	const breadcrumbPages = [
		{ name: 'Administraciones', href: './administraciones', current: false },
		{
			name: administration?.name,
			href: administration?.id,

			current: true,
		},
	]

	const tabs = [
		{ name: 'Trámites', href: '?tab=tramites', current: false },
		administration.Offices.length > 0 && {
			name: 'Oficinas',
			href: '?tab=oficinas',
			current: false,
		},
	]

	function classNames(...classes) {
		return classes.filter(Boolean).join(' ')
	}

	const [currentTab, setCurrentTab] = useState(
		tabs.find(tab => tab.href === '?tab=' + data.tabTerm) ?? tabs[0],
	)

	const handleTabChange = tab => {
		setCurrentTab(tab)
	}

	return (
		<>
			<div className="max-w-prose pt-12 text-lg">
				<Breadcrumb pages={breadcrumbPages} />
				<PageTitle
					title={administration?.name}
					sizeClasses="text-3xl sm:text-5xl lg:text-5xl"
				/>
			</div>

			<div>
				<div className="sm:hidden">
					<label htmlFor="tabs" className="sr-only">
						Select a tab
					</label>
					<select
						id="tabs"
						name="tabs"
						className="block w-full rounded-md border-gray-300 focus:border-amber-700 focus:ring-amber-700"
						onChange={e => handleTabChange(e.target.value)}
						value={currentTab.name}
					>
						{tabs.map(tab => (
							<option key={tab.name} value={tab.name}>
								{tab.name}
							</option>
						))}
					</select>
				</div>
				<div className="hidden sm:block">
					<div className="border-b border-gray-200">
						<nav className="-mb-px flex" aria-label="Tabs">
							{tabs.map(tab => (
								<a
									key={tab.name}
									href={tab.href}
									className={classNames(
										tab.name === currentTab.name
											? 'border-amber-700 text-amber-700'
											: 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
										'w-1/4 cursor-pointer border-b-2 px-1 py-4 text-center text-sm font-medium',
									)}
									onClick={() => handleTabChange(tab)}
									aria-current={
										tab.name === currentTab.name ? 'page' : undefined
									}
								>
									{tab.name}
								</a>
							))}
						</nav>
					</div>
				</div>
			</div>
			<div className="pt-8">
				{currentTab.name === 'Trámites' ? (
					<>
						{/* <DataTable data={procedures} /> */}
						<SearchBar
							status={data.status}
							autoFocus
							autoSubmit
							placeholder="¿Qué trámite buscas?"
						/>

						<ul className="mt-8 divide-y divide-gray-200">
							{procedures?.map(procedure => (
								<ProcedureListElement
									key={procedure.id}
									procedure={procedure}
								/>
							))}
						</ul>
						<div className="mt-8">
							<PaginationBar 
								currentPage={data.page || 1}
								totalPages={Math.ceil(data.totalCount / data.pageSize)}
								linkTo={(page) => `?page=${page}`}
							/>
						</div>
					</>
				) : (
					administration.Offices && (
						<AdminOffices offices={administration.Offices} />
					)
				)}
			</div>
		</>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const procedureAdministration = data?.administration.name ?? 'Papelea'
	return [
		{
			property: 'robots',
			content: 'index, follow',
		},
		{ title: procedureAdministration },
		{
			tagName: 'link',
			rel: 'canonical',
			href: `https://papelea.com/${params.country}/${params.administration}`,
		},
		{
			property: 'og:title',
			content: procedureAdministration,
		},
		{
			name: 'keywords',
			content: procedureAdministration + ', trámite, administraciones',
		},
		{
			property: 'og:type',
			content: 'website',
		},
		{
			property: 'og:url',
			content: `https://papelea.com/${params.country}/${params.administration}`,
		},
	]
}

export const handle: SEOHandle = serverOnly$({
	getSitemapEntries: async request => {
		const administrations = await getAllAdministrations()
		return administrations.map(administration => {
			return {
				route: `/${administration.country.code}/${administration.slug}`,
				priority: 0.7,
			}
		})
	},
})

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				403: () => <p>No está autorizado</p>,
				404: ({ params }) => (
					<p>
						No hay una administración con el nombre "{params.administration}"{' '}
					</p>
				),
			}}
		/>
	)
}
